import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import { Customer, CustomerSearchData } from '../../models/customer.model';
import { revertAll, RootState } from '../../store';
import { CustomerNote } from '../../models/customer.note.model';
import { CustomerContactItem } from '../../models/customer.contact.model';

export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (
    { company_id, page, name, sort, order, page_size }: CustomerSearchData,
    { rejectWithValue }
  ) => {
    if (company_id === undefined) {
      return rejectWithValue('Company ID is required');
    }
    const response = await NWClient.list('customer', {
      company_id,
      page,
      sort,
      name,
      order,
      page_size,
    });
    return response;
  }
);

export const fetchCustomerById = createAsyncThunk(
  'customers/fetchCustomerById',
  async (customerId: number, { rejectWithValue }) => {
    try {
      const response = await NWClient.get('customer', customerId);
      return response as Customer;
    } catch (error) {
      rejectWithValue(error.message);
    }
  }
);

export const updateCustomers = createAsyncThunk(
  'customers/updateCustomers',
  async (updatedCustomer: Customer, { rejectWithValue }) => {
    try {
      const response = await NWClient.put(
        'customer',
        updatedCustomer.id,
        updatedCustomer,
        true,
        false
      );
      return response as Customer;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCustomerNotes = createAsyncThunk(
  'customers/fetchCustomerNotes',
  async (customerId: number, { rejectWithValue }) => {
    if (customerId === undefined) {
      return rejectWithValue('Customer ID is required');
    }
    try {
      const response = (await NWClient.get('customer', customerId)) as Customer;
      return { customerId, notes: response.notes as CustomerNote[] };
    } catch (error) {
      return rejectWithValue('Failed to fetch customer notes');
    }
  }
);

export const updateCustomerNotes = createAsyncThunk(
  'customers/updateCustomerNotes',
  async ({ customerId, note }: { customerId: number; note: CustomerNote }, { dispatch }) => {
    try {
      await NWClient.put(`customer/${customerId}/notes`, note.id, note);
      // After successful update, fetch updated notes to refresh state
      await dispatch(fetchCustomerNotes(customerId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const deleteCustomerNote = createAsyncThunk(
  'customers/deleteCustomerNote',
  async ({ customerId, noteId }: { customerId: number; noteId: number }, { dispatch }) => {
    try {
      await NWClient.delete(`customer-notes`, noteId);
      // After successful deletion, fetch updated notes to refresh state
      await dispatch(fetchCustomerNotes(customerId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const createCustomerContactItem = createAsyncThunk(
  'customers/createCustomerContactItem',
  async (
    {
      customerId,
      contact,
    }: { customerId: number; contact: Partial<CustomerContactItem>; company_id: number },
    { dispatch }
  ) => {
    try {
      const formData = new FormData();

      Object.entries(contact).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      const response = await NWClient.post('customer-contacts', formData, true);
      await dispatch(fetchCustomerById(customerId));
      //await dispatch(fetchSuppliers({ company_id }));
      return { success: true, contact: response };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const updateCustomerContacts = createAsyncThunk(
  'customers/updateCustomerContacts',
  async (
    {
      customerId,
      contact,
    }: { customerId: number; contact: CustomerContactItem; companyId: number },
    { dispatch }
  ) => {
    try {
      const formData = new FormData();

      Object.entries(contact).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });
      await NWClient.put('customer-contacts', contact.id, formData, true, true);
      await dispatch(fetchCustomerById(customerId));
      //await dispatch(fetchSuppliers({ company_id: companyId }));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const deleteCustomerContacts = createAsyncThunk(
  'customer/deleteCustomerContacts',
  async (
    { contactId, customerId }: { companyId: number; contactId: number; customerId?: number },
    { dispatch }
  ) => {
    try {
      await NWClient.delete('customer-contacts', contactId);
      await dispatch(fetchCustomerById(customerId));
      //await dispatch(fetchSuppliers({ company_id: companyId }));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export interface CustomersState {
  customersList: Customer[];
  currentCustomerData: Customer;
  customersCount: number;
  status: string;
  error: string;
  notes: CustomerNote[];
}

const customersArray: Customer[] = [];
const initialState: CustomersState = {
  customersList: customersArray,
  currentCustomerData: null,
  customersCount: 0,
  status: 'idle',
  error: '',
  notes: [],
};

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearCustomerNotes(state) {
      state.notes = []; // Reset notes to an empty array
    },
    clearCustomersData(state) {
      state.currentCustomerData = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        if ('results' in action.payload && Array.isArray(action.payload.results)) {
          state.customersList = [...action.payload.results] as Customer[];
          state.customersCount = action.payload.count || 0;
          state.status = 'succeeded';
        }
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateCustomers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateCustomers.fulfilled, (state, action) => {
        const index = state.customersList.findIndex(
          (customer) => customer.id === action.payload.id
        );
        if (index !== -1) {
          state.customersList[index] = action.payload as Customer;
        }
        state.currentCustomerData = action.payload as Customer;
        state.status = 'succeeded';
      })
      .addCase(fetchCustomerById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const fetchedCustomer = action.payload as Customer;
        state.currentCustomerData = fetchedCustomer;
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(updateCustomers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchCustomerNotes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCustomerNotes.fulfilled, (state, action) => {
        const { notes } = action.payload;
        state.notes = notes;
        state.status = 'succeeded';
      })
      .addCase(fetchCustomerNotes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch customer notes';
      })
      .addCase(updateCustomerNotes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateCustomerNotes.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateCustomerNotes.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to update customer note';
      })
      .addCase(deleteCustomerNote.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteCustomerNote.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(deleteCustomerNote.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to delete customer note';
      })
      .addCase(createCustomerContactItem.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createCustomerContactItem.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(createCustomerContactItem.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to create supplier contact';
      })
      .addCase(updateCustomerContacts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateCustomerContacts.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateCustomerContacts.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to update supplier contact';
      })
      .addCase(deleteCustomerContacts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteCustomerContacts.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(deleteCustomerContacts.rejected, (state) => {
        state.status = 'failed';
        state.error = 'Failed to delete supplier contact';
      })
      .addCase(revertAll, () => initialState);
  },
});

export const { clearCustomerNotes, clearCustomersData } = customersSlice.actions;

export default customersSlice.reducer;

export const selectAllCustomers = (state: RootState) => state.customers.customersList;
export const selectCustomerById = (state: RootState, customerId: number) =>
  state.customers.customersList.find((item) => item.id === customerId);
export const selectCustomerNotes = (state: RootState) => state.customers.notes;
